import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { Schedule, UpdateScheduleOptions } from '@airreading/common/data';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Button } from '@/components/ui/button';
import { DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createSchedule, getSchedules, updateSchedule } from '@/lib/api-calls';
import { isNotErrorString } from '@/lib/common-utils';
import { parseIntoScheduleBreak } from '@/lib/date-utils';
import { textAreaValueToArray } from '@/lib/string-utils';

import { useAllSchools } from '@/lib/api-call-hooks';
import { validatePlainDateIntervalLike } from '@airreading/common';
import { formatScheduleBreakInput } from './formatting';

interface CreateEditScheduleProps {
  trigger?: ReactNode;
  modalTitle?: string;
  from?: {
    fromSchedule: Schedule;
    mode: 'edit' | 'create';
  };
  afterSave?: () => void;
}

const createEditScheduleFormSchema = z.object({
  scheduleBreaks: z
    .string()
    .optional()
    .superRefine((input, ctx) => {
      const scheduleBreaks = textAreaValueToArray(input);
      if (!scheduleBreaks.length) {
        return;
      }

      for (const scheduleBreak of scheduleBreaks) {
        const result = parseIntoScheduleBreak(scheduleBreak);
        if (typeof result === 'string') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: result,
          });
          return;
        }
      }
    }),
  startDate: z.instanceof(Temporal.PlainDate),
  endDate: z.instanceof(Temporal.PlainDate),
  practiceDate: z.instanceof(Temporal.PlainDate).nullable(),
  diagnosticDate: z.instanceof(Temporal.PlainDate).nullable(),
  schoolId: z.string(),
  name: z.string(),
});

const toDefaultValue = (
  fromSchedule?: Schedule,
  mode = 'create',
): DefaultValues<z.infer<typeof createEditScheduleFormSchema>> => {
  return {
    name: fromSchedule?.name ? `${fromSchedule.name}${mode === 'create' ? ' - copy' : ''}` : '',
    schoolId: fromSchedule?.schoolId,
    startDate: fromSchedule?.startPlainDate ?? Temporal.Now.plainDateISO(),
    endDate: fromSchedule?.endPlainDate ?? Temporal.Now.plainDateISO().add({ days: 1 }),
    practiceDate: fromSchedule?.practicePlainDate ?? null,
    diagnosticDate: fromSchedule?.diagnosticPlainDate ?? null,
    scheduleBreaks: (fromSchedule?.scheduleBreaks || []).map(formatScheduleBreakInput).join('\n'),
  };
};

export const CreateEditSchedule = ({ trigger, modalTitle, from, afterSave }: CreateEditScheduleProps) => {
  const { data: allSchools = [], isPending: isLoadingSchools } = useAllSchools();

  const { mutateAsync: createScheduleMutate } = useMutation({
    mutationFn: createSchedule,
  });

  const { mutateAsync: updateScheduleMutate } = useMutation({
    mutationFn: updateSchedule,
  });

  const fromSchedule = from?.fromSchedule;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const onSubmit = ({ formData, reset, closeModal }: SubmitProps<z.infer<typeof createEditScheduleFormSchema>>) => {
    const { startDate, endDate, practiceDate, diagnosticDate } = formData;
    const scheduleBreaks = textAreaValueToArray(formData.scheduleBreaks)
      .map((s) => parseIntoScheduleBreak(s))
      .filter(isNotErrorString);

    const selectedSchool = allSchools.find((o) => o.id === formData.schoolId);
    if (!selectedSchool) {
      toast({
        title: `Selected school not found`,
      });
      return;
    }
    const scheduleTimeZone = selectedSchool.org.timeZone;
    if (!scheduleTimeZone) {
      toast({
        title: 'Missing time zone',
        description:
          'Selected school does not have a time zone and you did not provide an override time zone. Either update the school to have a time zone or provide an override time zone.',
      });
      return;
    }

    if (!validatePlainDateIntervalLike(startDate, endDate)) {
      toast({
        title: 'Invalid date range',
        description: 'The start date must be before the end date.',
      });
      return;
    }

    const payload: UpdateScheduleOptions = {
      ...formData,
      startPlainDate: startDate,
      endPlainDate: endDate,
      timeZone: scheduleTimeZone,
      scheduleBreaks,
      practicePlainDate: practiceDate,
      diagnosticPlainDate: diagnosticDate,
    };

    const apiCall =
      fromSchedule && from.mode === 'edit'
        ? () =>
            updateScheduleMutate({
              id: fromSchedule.id,
              data: payload,
            })
        : () =>
            createScheduleMutate({
              ...payload,
              classTimes: [], // Created elsewhere after the schedule is created
            });

    return apiCall()
      .then(() => {
        toast({
          title: from?.mode === 'edit' ? 'Schedule updated' : 'Schedule created',
        });
        reset();
        closeModal();
        afterSave?.();
        queryClient.invalidateQueries(getSchedules());
      })
      .catch((err) => {
        toast({
          title: from?.mode === 'edit' ? 'Schedule update failed' : 'Schedule creation failed',
          description: err.message,
        });
      });
  };

  return (
    <FormModal
      modalTitle={modalTitle || 'Create schedule'}
      trigger={trigger || <Button>Create schedule</Button>}
      onSubmit={onSubmit}
      formSchema={createEditScheduleFormSchema}
      dialogContentProps={{ className: 'max-w-2xl' }}
      defaultValues={toDefaultValue(fromSchedule, from?.mode)}
    >
      {({ form }) => {
        return (
          <div>
            <DialogDescription>
              {from?.mode === 'edit'
                ? 'Modify the schedule details'
                : 'Create a schedule that can be used to create student groups and allow teacher signups'}
            </DialogDescription>
            <div className="grid grid-cols-2 gap-2 py-4">
              <FormInputField name="name" label="Schedule name" form={form} inputProps={{ className: 'w-[80%]' }} />
              <FormSingleSelect
                name="schoolId"
                label="School"
                form={form}
                options={allSchools.map((s) => s.id)}
                renderOption={(id) => allSchools.find((s) => s.id === id)?.name ?? id}
                loading={isLoadingSchools}
                triggerProps={{ className: 'w-[80%]' }}
              />
              <FormInputDate name="startDate" label="Start date" form={form} />
              <FormInputDate name="endDate" label="End date" form={form} />
              <FormInputDate name="practiceDate" label="Practice class date" form={form} />
              <FormInputDate name="diagnosticDate" label="Diagnostic class date" form={form} />
              <div className="col-span-2">
                <FormTextArea
                  name="scheduleBreaks"
                  label="Breaks (One per line, format 'MM/DD/YYYY - MM/DD/YYYY')"
                  form={form}
                  textAreaProps={{ placeholder: 'e.g. 03/01/2024 - 03/15/2024' }}
                />
              </div>
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};
